// src/pages/Home.jsx
import { Box, Typography, Card, Avatar, Chip, Divider } from '@mui/joy';
import Grid from '@mui/joy/Grid';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Announcements from '../components/Announcements';
import Links from '../components/Links';
import PermitAnalytics from '../components/PermitAnalytics';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import { usePermits } from '../hooks/usePermits';
import useSubmittalsDueSoon from '../hooks/useSubmittals';
import MyTasksWidget from '../components/StatusBoard/MyTasksWidget';

const Home = () => {
  const navigate = useNavigate();
  const { permits, expiringPermits } = usePermits();
  const { dueSoonSubmittals } = useSubmittalsDueSoon();
  const inProgressOrReview = permits.filter(p =>
    (p.start_date && !p.application_date) ||
    (p.application_date && !p.issue_date && !p.expiration_date)
  ).length;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const quickAccessItems = [
    {
      title: 'Permit Tracker',
      description: 'Monitor permit status and deadlines',
      icon: 'assignment',
      path: '/permits',
      color: '#00A884',
      gradient: ['#00A884', '#007E33']
    },
    {
      title: 'Submittal Tracker', 
      description: 'Track submittal progress and due dates',
      icon: 'task_alt',
      path: '/submittals',
      color: '#17a2b8',
      gradient: ['#17a2b8', '#138496']
    },
    {
      title: 'Client GIS Projects',
      description: 'Access interactive mapping tools',
      icon: 'satellite',
      path: '/gis-projects',
      color: '#fd7e14',
      gradient: ['#fd7e14', '#e8570c']
    }
  ];

  return (
    <PageLayout>
      <Box sx={{ 
        py: 3, 
        px: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(248, 250, 252, 1) 100%)'
      }}>
        {/* Hero Section */}
        <Card 
          sx={{ 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
            borderRadius: 'xl',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #00A884 0%, #007E33 100%)',
            }
          }}
          className="fade-in"
        >
          <Box sx={{ p: 6, textAlign: 'center', position: 'relative' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 3,
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                color: 'white',
                fontSize: '2.5rem',
                boxShadow: '0 8px 25px rgba(0, 168, 132, 0.25)',
              }}
              className="pulse-subtle"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '40px', fontFamily: 'Material Symbols Outlined' }}>
                grid_view
              </span>
            </Avatar>
            
            <Typography 
              level="h2" 
              sx={{ 
                mb: 2, 
                background: 'linear-gradient(135deg, #00A884, #007E33)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800,
                letterSpacing: '-0.025em'
              }}
            >
              {getGreeting()}, Welcome to The Grid
            </Typography>
            
            <Typography 
              level="h5" 
              sx={{ 
                color: 'text.secondary', 
                maxWidth: '600px', 
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Your comprehensive project management platform for permits, submittals, and deadlines
            </Typography>
          </Box>
        </Card>

  {/* My Tasks Widget for Staff - moved just below welcome */}
  <MyTasksWidget />

  {/* Dashboard Metrics */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100', bgcolor: 'success.50' }}>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 700
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
                dashboard
              </span>
              Dashboard Overview
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Real-time insights into your active projects and deadlines
            </Typography>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Active Permits"
                  value={permits.length}
                  icon="assignment"
                  onClick={() => navigate('/permits')}
                  gradientColors={['#00A884', '#007E33']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="In Progress"
                  value={inProgressOrReview}
                  icon="hourglass_empty"
                  onClick={() => navigate('/permits')}
                  gradientColors={['#17a2b8', '#138496']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Expiring Soon"
                  value={expiringPermits.length}
                  icon="schedule"
                  onClick={() => navigate('/permits')}
                  gradientColors={['#dc3545', '#c82333']}
                />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <MetricCard
                  title="Due Submittals"
                  value={dueSoonSubmittals.length}
                  icon="task_alt"
                  onClick={() => navigate('/submittals')}
                  gradientColors={['#ffc107', '#e0a800']}
                />
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Quick Access Grid */}
        <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden' }} className="fade-in-up">
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100', bgcolor: 'success.50' }}>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontWeight: 700
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
                apps
              </span>
              Quick Access
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Navigate directly to your most used tools and features
            </Typography>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
              {quickAccessItems.map((item, index) => (
                <Grid xs={12} sm={6} md={4} key={item.path}>
                  <Card
                    sx={{
                      p: 3,
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid',
                      borderColor: 'neutral.200',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 35px ${item.color}20`,
                        borderColor: item.color,
                        '& .quick-access-icon': {
                          transform: 'scale(1.1)',
                          color: 'white',
                          bgcolor: item.color,
                        }
                      }
                    }}
                    onClick={() => navigate(item.path)}
                    className="hover-lift"
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', height: '100%' }}>
                      <Avatar
                        className="quick-access-icon"
                        sx={{
                          width: 56,
                          height: 56,
                          mb: 2,
                          bgcolor: `${item.color}15`,
                          color: item.color,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
                          {item.icon}
                        </span>
                      </Avatar>
                      
                      <Typography level="title-md" sx={{ color: 'success.700', fontWeight: 600, mb: 1 }}>
                        {item.title}
                      </Typography>
                      
                      <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', flex: 1 }}>
                        {item.description}
                      </Typography>

                      <Chip 
                        size="sm" 
                        variant="soft" 
                        sx={{ 
                          mt: 2,
                          bgcolor: `${item.color}15`,
                          color: item.color,
                          transition: 'all 0.3s ease'
                        }}
                        startDecorator={
                          <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                            arrow_forward
                          </span>
                        }
                      >
                        Access
                      </Chip>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>

        {/* Content Sections */}
        <Grid container spacing={4}>
          {/* Announcements */}
          <Grid xs={12} md={6}>
            <Card sx={{ borderRadius: 'xl', overflow: 'hidden', height: 'fit-content' }} className="fade-in-up">
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'primary.100', bgcolor: 'primary.50' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'primary.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontWeight: 700
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    campaign
                  </span>
                  Latest Announcements
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Stay updated with important notifications
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Announcements />
              </Box>
            </Card>
          </Grid>

          {/* Quick Links */}
          <Grid xs={12} md={6}>
            <Card sx={{ borderRadius: 'xl', overflow: 'hidden', height: 'fit-content' }} className="fade-in-up">
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'warning.100', bgcolor: 'warning.50' }}>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'warning.700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    fontWeight: 700
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                    link
                  </span>
                  Quick Links
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Fast access to external resources and tools
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Links />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
};

export default Home;